/**
 * Modal — interne implementatie aan de Aurora-v2-spec (§8.9).
 *
 * Niet uit het bcs-aurora-design-system package — dat levert geen
 * Modal/Drawer-component, alleen de spec. We bouwen 'm hier vanuit
 * de tokens + spec-regels:
 *
 *  - Centered, max 480/640/800 px (size = sm/md/lg)
 *  - Backdrop overlay + blur (surface-overlay)
 *  - radius-xl, scale(0.95)→1 entrance
 *  - role="dialog", aria-modal, focus-trap, ESC close, focus return
 *
 * Header/body/footer-slots via composition; `open` controleert
 * visibility zodat de consumer de levenscyclus bezit (ADR-0006-stijl).
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { useAuroraLabels } from '../auroraLabels';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** sm = 480px (default), md = 640px, lg = 800px */
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
  footer?: ReactNode;
  /**
   * Sluit via ESC + close-knop in de header. Standaard true. Zet op
   * false voor irreversible-action modals waar je sluiten alleen via
   * een expliciete footer-knop wil.
   */
  dismissible?: boolean;
  /**
   * Sluit ook bij klikken op de backdrop. Standaard FALSE — een
   * per-ongeluk klik tijdens typen mag een form-modal niet weggooien.
   * Opt-in voor read-only / informatieve modals waar backdrop-click
   * een normale exit-route is.
   */
  closeOnBackdrop?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  size = 'sm',
  children,
  footer,
  dismissible = true,
  closeOnBackdrop = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // LOKALE afwijking (issue 294): de naam van de sluitknop komt uit de
  // AuroraLabels-context in plaats van een vaste Engelse "Close".
  const labels = useAuroraLabels();

  /*
   * `onClose` en `dismissible` via ref zodat we ze NIET in de dep-array
   * van het mount-effect hoeven te zetten. Anders zou een inline
   * `onClose={() => ...}` in de caller elke render een nieuwe ref geven,
   * waardoor dit effect remount: cleanup focust 'previouslyFocusedRef'
   * (de trigger-knop) en de fresh run focust de dialog-div. Resultaat:
   * elke parent-state-change (zoals typen in een Input binnen de modal)
   * trapt de focus weg uit het input-veld. Klassieke gotcha — zie
   * commit-message van deze fix voor de full reproductie-flow.
   */
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  useEffect(() => {
    onCloseRef.current = onClose;
    dismissibleRef.current = dismissible;
  });

  // ESC sluit + lock body-scroll terwijl de modal open is. Dep-array
  // bewust ALLEEN `open` — de callbacks komen uit refs (zie boven).
  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && dismissibleRef.current) {
        e.stopPropagation();
        onCloseRef.current();
      }
    }
    document.addEventListener('keydown', onKey);

    // Focus naar de dialog zelf zodat assistive tech de modal aankondigt.
    dialogRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      // Focus terug naar het element dat 'm opende (a11y-vereiste).
      previouslyFocusedRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const titleId = 'aurora-modal-title';

  return (
    <div className={styles.backdrop} onClick={closeOnBackdrop ? onClose : undefined}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={[styles.dialog, styles[size]].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>{title}</h2>
          {dismissible && (
            <button
              type="button"
              className={styles.closeBtn}
              aria-label={labels.modalClose}
              onClick={onClose}
            >
              <X size={18} weight="regular" aria-hidden="true" />
            </button>
          )}
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>
  );
}
